import AddIcon from '@atlaskit/icon/core/add';
import { token } from '@atlaskit/tokens';
import { DraggableList } from '@databases/draggable-list/DraggableList';
import { FormButton } from '@databases/form/styled';
import { messages } from '@databases/intl/messages';
import { useFocusInputField } from '@databases/react';
import classNames from 'classnames';
import { produce } from 'immer';
import React, { type ReactElement, type ReactNode, useCallback, useRef } from 'react';
import { useIntl } from 'react-intl-next';
import styled from 'styled-components';

const Container = styled.div<{
  verticalScroll?: boolean;
  isViewsList?: boolean;
}>`
  display: flex;
  flex-direction: column;
  max-height: ${(props) =>
    props.verticalScroll
      ? '395px'
      : 'none'}; // introduces scroll after 8 cards are inserted
  padding: ${(props) => (props.isViewsList ? '6px' : '')};
`;

const ContainerWithScroll = styled.div`
  max-height: ${(props) => (props.verticalScroll ? '395px' : 'none')};
  overflow-y: auto;
  overflow-x: ${(props) => (props.verticalScroll ? 'hidden' : 'auto')};
  padding-bottom: ${token('space.025', '2px')};
  padding-left: ${token('space.050', '4px')};
  padding-right: ${token('space.025', '2px')};
  padding-top: ${token('space.050')};
`;

const AddButtonContainer = styled.div`
  background: ${token('elevation.surface.overlay', '#FFFFFF')};
  margin: 0 ${token('space.025', '2px')};

  && button {
    padding: 0 ${token('space.150', '12px')};
`;

const subtleBorder = `2px solid ${token('color.border')}`;

const Hr = styled.hr`
  border: none;
  border-top: ${subtleBorder};
  width: 100%;
`;

export interface EditableListProps<T, U>
  extends Omit<
    RawEditableListProps<T, U>,
    'onAdd' | 'onValueChange' | 'onDelete' | 'onReorder' | 'autoFocus'
  > {
  /** TODO NCTD: deprecated. Use new incremental callbacks instead. - delete this when all usages are updated*/
  onChange?: (values: T[]) => void;
  onItemChange?: (value: T, newIndex: number, delta: U, allValues: T[]) => void;
  onAddNewItem?: (value: T, allValues: T[]) => void;
  onRemoveItem?: (value: T, removeIndex: number, allValues: T[]) => void;
  onReorder?: (value: T, currentIndex: number, newIndex: number, allValues: T[]) => void;
  getNewValue: () => T;
  autoFocus?: boolean;
  isViewsList?: boolean;
  isHovering?: boolean;
  onBlur?: () => void;
  onFocus?: () => void;
  allowDuplicates?: boolean;
}

/**
 * Renders a vertical list of text input fields that can be edited, reordered, deleted and added.
 */
export function EditableList<T, U>({
  values,
  onChange,
  onItemChange,
  onAddNewItem,
  onRemoveItem,
  onReorder,
  getNewValue,
  autoFocus,
  isViewsList,
  isHovering,
  allowDuplicates = true,
  ...props
}: EditableListProps<T, U>): ReactElement {
  const focusI = useRef<number | undefined>();

  const handleAdd = useCallback(() => {
    focusI.current = values.length;
    const newValue = getNewValue();
    const allValues = [...values, newValue];
    if (onAddNewItem) {
      onAddNewItem(newValue, allValues);
    } else {
      onChange?.(allValues);
    }
  }, [getNewValue, onChange, onAddNewItem, values]);

  const [duplicateId, setDuplicateId] = React.useState<string | undefined>(undefined);
  const handleValueChange = useCallback(
    (i: number, val: T, delta: U) => {
      const newValues = produce(values, (newValues) => {
        newValues[i] = val as any;
      });
      const filteredLabels = newValues
        .filter((item: any) => item.id !== (val as any).id)
        .map((item: any) => item.label);
      if (!allowDuplicates) {
        if (filteredLabels.includes((val as any).label)) {
          setDuplicateId((val as any).id);
          return;
        } else {
          setDuplicateId(undefined);
        }
      }
      if (onItemChange) {
        onItemChange(val, i, delta, newValues);
      } else {
        onChange?.(newValues);
      }
    },
    [onChange, onItemChange, values, allowDuplicates]
  );

  const handleReorder = useCallback(
    (i: number, newIdx: number) => {
      const newValues = produce(values, (newValues) => {
        const [removed] = newValues.splice(i, 1);
        newValues.splice(newIdx, 0, removed);
      });
      if (onReorder) {
        onReorder(values[i], i, newIdx, newValues);
        // If the reordered item had the focus, move the focus to same item so that the user can blur properly to exit the inline edit field
        focusI.current = newIdx;
      } else {
        onChange?.(newValues);
      }
    },
    [onChange, onReorder, values]
  );

  const handleDelete = useCallback(
    (i: number) => {
      // If the deleted item had the focus, move the focus to another item so that the user can blur properly to exit the inline edit field
      focusI.current = i === values.length - 1 && i > 0 ? i - 1 : i;
      setDuplicateId(undefined);

      const newValues = produce(values, (newValues) => {
        newValues.splice(i, 1);
      });
      if (onRemoveItem) {
        onRemoveItem(values[i], i, newValues);
      } else {
        onChange?.(newValues);
      }
    },
    [onChange, onRemoveItem, values]
  );

  let resolvedFocusI: number | 'addButton' | undefined =
    focusI.current ?? (autoFocus ? 0 : undefined);
  if (typeof resolvedFocusI === 'number' && resolvedFocusI >= values.length) {
    resolvedFocusI = values.length === 0 ? 'addButton' : values.length - 1;
  }
  focusI.current = undefined;

  return (
    <RawEditableList<T, U>
      {...props}
      values={values}
      onAdd={handleAdd}
      onValueChange={handleValueChange}
      onDelete={handleDelete}
      onReorder={handleReorder}
      autoFocus={resolvedFocusI}
      isViewsList={isViewsList}
      isHovering={isHovering}
      duplicateId={duplicateId}
    />
  );
}

interface RawEditableListProps<T, U> {
  getId: (value: T) => string;
  values: T[];
  onAdd?: () => void;
  onValueChange?: (i: number, value: T, delta: U) => void;
  onDelete?: (i: number) => void;
  onReorder?: (i: number, newIdx: number) => void;
  renderField: (data: {
    i: number;
    value: T;
    autoFocus: boolean;
    onChange: (value: T, delta: U) => void;
    isReadonly: boolean;
    isDisabled: boolean;
  }) => ReactNode;
  /** The index of the item to autofocus. */
  autoFocus?: number | 'addButton';
  addLabel?: string;
  noAdd?: boolean;
  /** If true, show a divider above the add button */
  showDivider?: boolean;
  noDelete?: boolean;
  className?: string;
  isHovering?: boolean;
  dragAxis?: 'x' | 'y';
  yAxisBounds?: boolean;
  isReadonly?: boolean;
  isDisabled?: boolean;
  verticalScroll?: boolean;
  activeId?: string;
  editingId?: string;
  errorId?: string;
  showUnsavedView?: boolean;
  isViewsList?: boolean;
  onBlur?: () => void;
  onFocus?: () => void;
  duplicateId?: string | undefined;
}

/**
 * Renders a vertical list of text input fields that can be edited, reordered, deleted and added.
 * Does not manage the array of items, but offers event handlers for the individual user actions to react to.
 */
export function RawEditableList<T, U>({
  getId,
  values,
  onAdd,
  onValueChange,
  onDelete,
  onReorder,
  autoFocus,
  renderField,
  addLabel,
  noAdd,
  noDelete,
  className,
  isHovering = true,
  showDivider = false,
  isReadonly = false,
  isDisabled = false,
  verticalScroll = false,
  activeId = undefined,
  editingId = undefined,
  errorId = undefined,
  isViewsList = false,
  onBlur,
  onFocus,
  duplicateId,
}: RawEditableListProps<T, U>): ReactElement {
  const ulRef = useRef<HTMLUListElement>(null);
  const addButtonRef = useRef<HTMLElement>(null);

  const { formatMessage } = useIntl();

  useFocusInputField(
    () => {
      if (typeof autoFocus === 'number') {
        return ulRef.current?.childNodes[autoFocus] as HTMLElement | undefined;
      } else if (autoFocus === 'addButton') {
        return addButtonRef.current;
      }
    },
    autoFocus === 'addButton' ? '*' : undefined
  );

  const lastYPosition = useRef(0);
  const handleAdd = useCallback(() => {
    lastYPosition.current = 0;
    onAdd?.();
  }, [onAdd]);

  const handleBlur = useCallback(
    (e: React.FocusEvent) => {
      if (
        e.currentTarget.contains(e.relatedTarget) ||
        e.relatedTarget?.id.includes('ds--dropdown')
      ) {
        e.stopPropagation();
      } else {
        onBlur?.();
      }
    },
    [onBlur]
  );

  return (
    <Container
      isViewsList={isViewsList}
      className={classNames([className])}
      verticalScroll={verticalScroll}
      onBlur={handleBlur}
      onFocus={onFocus}
    >
      <ContainerWithScroll verticalScroll={verticalScroll} isHovering={isHovering}>
        <DraggableList
          onReorder={onReorder}
          renderField={renderField}
          values={values}
          getId={getId}
          isReadonly={isReadonly}
          isDisabled={isDisabled}
          autoFocus={autoFocus}
          onValueChange={onValueChange}
          noDelete={noDelete}
          onDelete={onDelete}
          activeId={activeId}
          errorId={errorId}
          editingId={editingId}
          isHovering={isHovering}
          duplicateId={duplicateId}
        />
      </ContainerWithScroll>
      {!noAdd && !isReadonly && duplicateId === undefined && (
        <AddButtonContainer>
          {showDivider && <Hr />}
          <FormButton
            iconBefore={<AddIcon label="" LEGACY_size="small" />}
            onClick={handleAdd}
            ref={addButtonRef}
            isDisabled={isDisabled}
            testId="editable-list-add-button"
          >
            {addLabel ? addLabel : formatMessage(messages['field-settings.tag-add-item'])}
          </FormButton>
        </AddButtonContainer>
      )}
    </Container>
  );
}
